/* eslint-disable react-compiler/react-compiler */

import * as Random from "@/realize-shared/Random";
import backgroundTexture from "@/assets/img/layout/profile/headline-creator/background-texture.webp";
import magazine from "@/assets/img/layout/profile/headline-creator/magazine.webp";
import centerBackground from "@/assets/img/layout/profile/headline-creator/center-background.webp";
import OptionsBackground from "@/assets/img/layout/profile/headline-creator/optionsBackground.svg?react";
import IconPencil from "@/assets/img/symbolic/pencil.svg?react";
import IconDice from "@/assets/img/symbolic/dice.svg?react";
import IconCharacters from "@/assets/img/symbolic/characters.svg?react";
import IconExit from "@/assets/img/symbolic/close.svg?react";
import PrevHeadline from "@/assets/img/layout/profile/headline-creator/previous-headline.svg?react";
import NextHeadline from "@/assets/img/layout/profile/headline-creator/next-headline.svg?react";
import Padding from "../shared/Padding";
import JaggedButton from "../JaggedButton";
import AutoComplete from "@/game/AutoComplete";
import type * as ReactTypes from "@/game/types/ReactTypes";
import * as Sanitization from "@/realize-shared/Sanitization";
import { sanitizeInputElement } from "@/util/ElementSanitation";
import React, { forwardRef, useCallback, useRef, useState } from "react";
import social from "@/api/social";
import HTTPStatus from "@/realize-shared/HTTPStatus";
import GlobalGUI from "@/util/GlobalGUI";

const MAX_INPUT_LENGTH = 100;

type Handles<T> = Record<string, T | null>;

function HeadlineCreatorButton({
    text,
    icon,
    onClick,
}: {
    text: string;
    icon: React.ReactNode;
    onClick: () => void;
})
{
    return (
        <button
            className={Wind.style({
                color: "text-dark-blue",
                fontWeight: "font-medium",
                fontFamily: "font-poppins",
                textWrap: "text-balance",
                height: "h-full",
                display: "flex",
                flexDirection: "flex-col",
                alignItems: "items-center",
                justifyContent: "justify-end",
            }).class}
            onClick={onClick}
        >
            {icon}
            <span>
                {text}
            </span>
        </button>
    );
}


/**
 * Text entry pop-up to allow user to type in custom headline
 */
const HeadlineTextInputContainer = ({
    visible,
    hide,
    submit,
    headline,
}: {
    visible: boolean;
    hide: () => unknown;
    headline: string;
    submit: (headline: string) => unknown;
}) =>
{
    const inputRef = React.useRef<HTMLTextAreaElement>(null);

    // grow/shrink to show text
    const reflow = () =>
    {
        if (!inputRef.current) return;

        inputRef.current.style.height = "0";
        inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    };

    // heighlight input
    React.useMemo(() =>
    {
        if (!inputRef.current || !visible) return;

        const text = headline.charAt(0).toUpperCase() + headline.slice(1);

        inputRef.current.value = text;

        inputRef.current.focus();
        inputRef.current.setSelectionRange(text.length, text.length);

        reflow();
    }, [visible, headline]);

    const send = () =>
    {
        const input = inputRef.current;

        if (!input) return;

        const length = input.value.length;

        if (length > MAX_INPUT_LENGTH) return;

        submit(sanitizeInputElement(
            input,
            Sanitization.sanitizeTextBlock,
        ));
    };

    return (
        <div
            className={`
                fixed
                inset-0
                bg-black
                z-50
                backdrop-blur
                transition-opacity
                duration-100
                ${visible && "opacity-80"}
                ${visible? "block": "invisible"}
            `}
            id="custom-headline-backdrop"
            onClick={(e) =>
            {
                if (e.target === e.currentTarget) hide();
            }}
        >
            <Padding
                top
                bottom
                wrapper={Wind.style({
                    position: "relative",
                    width: "w-full",
                    height: "h-full",
                    backgroundColor: "bg-black",
                    opacity: "opacity-100",
                }).class}
                onClick={(e) =>
                {
                    if (e.target === e.currentTarget) hide();

                    const element = e.target as HTMLDivElement;

                    if (element.classList.contains("padding-wrapper")) hide();
                }}
            >
                <button
                    className={`
                        absolute
                        block
                        right-4
                        mt-4
                        px-4
                        py-2
                        cancelButton
                        z-[1000]
                        text-white
                    `}
                    style={{
                        "paddingTop": "var(--safe-area-top)",
                    }}
                    onClick={hide}
                >
                    <IconExit
                        className={`
                            w-8
                            h-8
                        `}
                    />
                </button>
                <div
                    className={`
                        absolute
                        px-4
                        w-full
                    `}

                    style={{
                        bottom: "calc(var(--keyboard-height) + 1rem)"
                    }}
                >
                    <div
                        className={`
                            flex
                            justify-between
                            items-center
                        `}
                    >
                        <p
                            className={`
                                mb-1
                                text-white
                                text-opacity-70
                                text-xs
                            `}
                        >
                            Type in a Headline:
                        </p>
                        <div
                            className={`
                                flex
                                items-center
                                text-white
                                text-opacity-70
                            `}
                        >
                            {inputRef.current?.value.length ?? 0}
                            {" / "}
                            {MAX_INPUT_LENGTH}
                        </div>
                    </div>
                    <div
                        className={`
                            p-2
                            border
                            border-white
                            flex
                        `}
                    >
                        <textarea
                            className={`
                                flex-[1_1_100%]
                                bg-transparent
                                placeholder:text-left
                                text-white
                                border-none
                                focus:outline-none
                            `}

                            placeholder="Realize..."
                            name="headline"
                            inputMode="text"
                            rows={1}

                            ref={inputRef}

                            onInput={(e) =>
                            {
                                e.currentTarget.value = sanitizeInputElement(
                                    e.currentTarget,
                                    Sanitization.sanitizeTextBlock,
                                );

                                if (e.currentTarget.value.length > MAX_INPUT_LENGTH)
                                {
                                    e.currentTarget.value = e.currentTarget.value.slice(0, MAX_INPUT_LENGTH);
                                }

                                reflow();
                            }}

                            onKeyDown={(e) =>
                            {
                                if (e.key !== "Enter") return;

                                send();
                            }}
                        />
                        <button
                            className="
                                text-black
                                bg-white
                                px-2
                                rounded-lg
                            "
                            onClick={() =>
                            {
                                send();
                            }}
                        >
                            Create
                            {/* <SendIcon /> */}
                        </button>
                    </div>
                </div>
            </Padding>
        </div>
    );
};

/**
 * Scrollable pop-up to select single word
 */
const WordHelperPopup = (props: {
    setShowOptions: ReactTypes.SetState<boolean>;
    scrollingArea: React.RefObject<HTMLDivElement>;
    options: string[];
    setSelectedOption: (option: number) => void;
    selector: React.RefObject<HTMLDivElement>;
}) =>
{
    const {
        setShowOptions,
        scrollingArea,
        options,
        setSelectedOption,
        selector,
    } = props;

    const [tempSelected, setTempSelected] = React.useState(0);

    const setDataID = (index: number) =>
    {
        if (!selector.current) return;

        // @FIXME I don't know how to fix this error
        // I think believe it is a side-effect of using forwardRef, which is in the process of being deprecated

        selector.current.dataset.id = index.toString();
    };

    const getWordlist = () =>
    {
        let previousLetter = "";

        return options.map((option, index) =>
        {
            const firstLetterOfWord = option
                .replaceAll(/\*/ug, "")
                .replace(/^(?:an?|the) /giu, "")[0];

            const newLetter = firstLetterOfWord !== previousLetter;
            const selectedClasses = (index === tempSelected)? "bg-sharp-red bg-opacity-10": "";

            if (newLetter) previousLetter = firstLetterOfWord;

            return (
                <div
                    key={index}
                    className={
                        `text-dark-blue font-default font-semibold
                        text-base py-1 leading-6 ${selectedClasses}`
                    }
                    onClick={() => void setTempSelected(index)}
                >
                    {newLetter && (
                        <div
                            className={
                                `float-left uppercase text-secondary-gray font-default
                                text-base font-bold leading-6 ml-2 relative w-4`
                            }
                            id={`letter-${firstLetterOfWord.toLowerCase()}`}
                        >
                            {firstLetterOfWord}
                        </div>
                    )}
                    <div
                        className={`${newLetter ? "-ml-6 w-full absolute" : ""}}`}
                    >
                        {option.replace(/\*/gu, "")}
                    </div>
                </div>
            );
        });
    };

    return (
        <div
            className="fixed inset-0 bg-black z-50 bg-opacity-75"
            onClick={(e) =>
            {
                if (e.target === e.currentTarget)
                {
                    setShowOptions(false);

                    // Remove the pop-over class from the body. This shows the chat.
                    document.body.classList.remove("pop-over");
                }
            }}
        >
            <div
                className={`
                    bg-parchment
                    w-full
                    absolute
                    bottom-0
                    pb-[var(--safe-area-bottom)]
                    h-[75vh]
                    shadow-[0px_-5px_15px_-5px_rgba(0,0,0,0.5)]
                    flex
                    flex-col
                `}
            >
                <h2
                    className={
                        "py-2 px-3 text-dark-blue font-bold leading-normal text-left"
                    }
                >
                    Word Selection
                </h2>
                <div
                    className="py-2 overflow-y-auto flex flex-col gap-2"
                    ref={scrollingArea}
                >
                    {getWordlist()}
                </div>
                <div className="py-4 flex items-center justify-end px-3 gap-2">
                    <button
                        className={
                            `bg-dark-blue rounded lowercase text-white
                            text-base font-default font-normal py-2 w-32`
                        }
                        onClick={() =>
                        {
                            setShowOptions(false);

                            // Remove the pop-over class from the body. This shows the chat.
                            document.body.classList.remove("pop-over");
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className={
                            `bg-sharp-red rounded text-white lowercase
                            text-base font-default font-normal py-2 w-32`
                        }
                        onClick={() =>
                        {
                            setDataID(tempSelected);
                            setSelectedOption(tempSelected);
                            setShowOptions(false);

                            // Remove the pop-over class from the body. This shows the chat.
                            document.body.classList.remove("pop-over");
                        }}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Yellow highlighted word in mad-lib headline
 */
const Highlighted = (props: {
    text: string;
}) =>
{
    const { text } = props;

    // skip "*", "**", and any word NOT surrounded by "*"
    const plain = text.length <= 2 || !text.startsWith("*") || !text.endsWith("*");

    if (plain) return text;

    const stripped = text.substring(2, text.length-2);

    return (
        <span className="font-bold">
            {stripped}
        </span>
    );
};

interface AutoFillSectionHandle
{
    getIndex: () => number;
    getSection: () => Word;
    setSection: (section: Word) => void;
}
type AutoFillSectionHandles = Handles<AutoFillSectionHandle>;

const AutoFillSection = forwardRef((props: {
    section: Word;
    self: number;
    setSelected: (index: number) => void;
}, ref: React.Ref<AutoFillSectionHandle>) =>
{
    const {
        setSelected,
    } = props;

    const scrollingArea = React.useRef<HTMLDivElement>(null);
    const selector = React.useRef<HTMLDivElement>(null);

    const [section, setSection] = React.useState(props.section);
    const [showOptions, setShowOptions] = React.useState(false);

    React.useImperativeHandle(ref, () => ({
        setSection,
        "getIndex":   () => props.self,
        "getSection": () => section,
    }));

    React.useEffect(() =>
    {
        // Auto Scroll To Selected Option
        const element = scrollingArea.current?.children[section[1]];

        if (!element) return;

        element.scrollIntoView({
            "behavior": "auto",
            "block":    "center",
            "inline":   "center",
        });
    }, [showOptions, section]);

    return (
        <>
            <div
                className={
                    `inline-block mx-1 bg-poppy-yellow text-dark-blue px-4 my-1
                    uppercase font-normal text-2xl leading-[110%] auto-fill-input`
                }
                onClick={() =>
                {
                    setShowOptions(!showOptions);

                    // Add the pop-over class to the body. This hides the chat.
                    if (!document.body.classList.contains("pop-over"))
                    {
                        document.body.classList.add("pop-over");
                    }
                }}
                data-id={section[1]}
                ref={selector}
            >
                <Highlighted text={section[0][section[1]]} />
            </div>
            {showOptions && (
                <WordHelperPopup
                    setShowOptions={setShowOptions}
                    scrollingArea={scrollingArea}
                    options={section[0]}
                    setSelectedOption={(index) =>
                    {
                        setSelected(index);
                    }}
                    selector={selector}
                />
            )}
        </>
    );
});

AutoFillSection.displayName = "AutoFillSection";

type Word = [suggestions: string[], selected: number];
type Section = string | Word;
type Template = Section[];

const parseTemplate = (section: string): Section =>
{
    const parsed = AutoComplete.get(section);

    return (parsed)
        ? [parsed, Random.integer(parsed.length)] // interactive
        : section;
};

interface HeadlinePromptHandle
{
    reroll: () => void;
    getTemplate: () => Template;
}

type HeadlinePromptHandles = Record<string, HeadlinePromptHandle | null>;

const HeadlinePrompt = forwardRef((props: {
    template: Template;
    self: React.Key;
    update: (template: Template) => void;
}, ref: React.Ref<HeadlinePromptHandle>) =>
{
    const {
        self,
        template,
        update,
    } = props;

    const [elements, setElements] = useState<React.JSX.Element[]>([]);
    const wordRefs = useRef<AutoFillSectionHandles>({});

    const fillInWord = React.useCallback((options: Word, index: number, key: string) =>
    {
        return (
            <AutoFillSection
                section={options}
                setSelected={(selected: number) =>
                {
                    const current: Section = [options[0], selected];

                    wordRefs.current[key]?.setSection(current);

                    // @TODO: overhaul this component
                    template[index] = current;

                    update(template);
                }}
                key={key}
                self={index}
                ref={(element) => (wordRefs.current[key] = element)}
            />
        );
    }, [template, update]);

    const rawString = (element: string, key: React.Key) => (
        <span
            key={key}
            className={
                "font-default text-white text-2xl font-medium"
            }
            style={{
                "textShadow": "0px 2px 2px rgba(0, 0, 0, 0.40)",
            }}
        >
            {element}
        </span>
    );

    const asElement = React.useCallback((element: Section, index: number) =>
    {
        const key = `${self},${index}`;

        return (Array.isArray(element))
            ? fillInWord(element, index, key)
            : rawString(element, key);
    }, [fillInWord, self]);

    React.useImperativeHandle(ref, () => ({
        reroll ()
        {
            Object.values(wordRefs.current).forEach((word) =>
            {
                if (!word) return;

                const index = word.getIndex();
                const prev = word.getSection();
                const current: Section = [prev[0], Random.integer(prev[0].length)];

                word.setSection(current);

                template[index] = current;
            });

            update(template);
        },
        getTemplate ()
        {
            return template;
        },
    }));

    React.useMemo(() =>
    {
        if (template.length === 0) throw new Error("Empty template");

        setElements(template.map(asElement));
    }, [asElement, template]);

    return (
        <div className="w-full py-6 mb-4">
            <div className="text-center px-2 first-letter:uppercase">
                {elements}
            </div>
        </div>
    );
});

HeadlinePrompt.displayName = "HeadlinePrompt";

const templateToString = (template: Template) => template
    .map((section) =>
    {
        if (typeof section === "string") return section;

        return section[0][section[1]];
    })
    .join(" ");

export default function HeadlineCreator({
    onClose,
}: {
    onClose?: (refresh?: boolean) => void;
})
{
    const [headline, setHeadline] = useState("");

    const [showHeadlineHelpers, setShowHeadlineHelpers] = React.useState(false);

    const [history, setHistory] = useState<React.JSX.Element[]>([]);
    const [count, setCount] = useState<number>(0);
    const [displayed, setDisplayed] = useState<number>(-1);

    const [isSubmitting, setIsSubmitting]= useState(false);

    const headlineRefs= useRef<HeadlinePromptHandles>({});

    const getHeadline = useCallback(() =>
    {
        const index = history.length;
        const rawTemplate = AutoComplete.templates[Random.integer(AutoComplete.templates.length)];

        let template = rawTemplate.map(parseTemplate);

        const phrase = (
            <HeadlinePrompt
                ref={(element) => (headlineRefs.current[index] = element)}
                key={count}
                self={count}
                template={template}
                update={(current: Template) =>
                {
                    template = [...current];

                    changeHeadline(template);
                }}
            />
        );

        setCount(count+1);
        setDisplayed(index);

        return phrase;
    }, [count, history]);

    const pushHeadline = React.useCallback(() =>
    {
        setHistory([...history, getHeadline()]);
    }, [getHeadline, history]);

    const previousHeadline = () =>
    {
        setDisplayed(Math.max(displayed-1, 0));
    };

    const nextHeadline = () =>
    {
        if (displayed >= history.length - 1)
        {
            pushHeadline();
        }
        else
        {
            setDisplayed(displayed + 1);
        }
    };

    const changeHeadline = (
        input: string | Template,
    ) =>
    {
        if (typeof input !== "string") input = templateToString(input);

        input = Sanitization.sanitizeTextBlock(input);

        Logger.log(input);

        setHeadline(input);

        return input;
    };

    const mixWords = () =>
    {
        headlineRefs.current[displayed]?.reroll();
    };

    React.useMemo(() =>
    {
        if (history.length <= 0) pushHeadline();
    }, [history.length, pushHeadline]);

    React.useEffect(() =>
    {
        const template = headlineRefs.current[displayed]?.getTemplate();

        if (!template) return;

        changeHeadline(template);
    }, [displayed]);

    React.useEffect(() =>
    {
        // Set StatusBar to dark mode
        GlobalGUI.setStyle({
            statusbarStyle: "dark",
        });
    }, []);

    const handleSubmission = (submission: string) =>
    {
        setIsSubmitting(true);

        social.createHeadline(submission).then((data) =>
        {
            if (typeof data === "object")
            {
                if (data.status === HTTPStatus.OK)
                {
                    Logger.log("Headline created successfully");
                    onClose?.(true);
                    setHistory([]);
                    setDisplayed(-1);
                    setHeadline("");
                    setCount(0);
                    setShowHeadlineHelpers(false);
                    // Reset the headlineRefs
                    headlineRefs.current = {};
                }
                else
                {
                    Logger.error("Error creating headline:", data);
                }
            }
            else
            {
                Logger.error("Unexpected response from server:", data);
            }
        })
            .catch((error) =>
            {
                Logger.error(error);
            })
            .finally(() =>
            {
                setIsSubmitting(false);
            });
    };

    return (
        <>
            <div
                className={Wind.style({
                    position: "relative",
                    width: "w-full",
                    height: "h-full",
                    backgroundPosition: "bg-center",
                    backgroundSize: "bg-cover",
                    backgroundColor: "bg-[#0F1F2B]",
                }).class}
                style={{
                    backgroundImage: `url(${backgroundTexture})`,
                }}
            >
                <img
                    src={magazine}
                    className={
                        Wind.style({
                            position: "absolute",
                            top: "top-0",
                            left: "left-0",
                            width: "w-full",
                            height: "h-full",
                            objectFit: "object-cover",
                            zIndex: "z-0",
                        }).class
                    }
                />
                <Padding
                    top
                    bottom
                    className={Wind.style({
                        color: "text-white",
                        display: "flex",
                        flexDirection: "flex-col",
                        justifyContent: "justify-between",
                    }).class}
                >
                    <div
                        className={Wind.style({
                            width: "w-full",
                            paddingX: "px-4",
                        }).class}
                    >
                        <button
                            onClick={() =>
                            {
                                onClose?.();
                            }}
                            className={Wind.style({
                                position: "relative",
                                width: "w-10",
                                height: "h-10",
                                backgroundColor: "bg-[#0F1F2B]",
                                borderRadius: "rounded-full",
                                display: "flex",
                                alignItems: "items-center",
                                justifyContent: "justify-center",
                            }).class}
                        >
                            <IconExit
                                className={Wind.style({
                                    width: "w-8",
                                    height: "h-8",
                                }).class}
                            />
                        </button>
                    </div>
                    <div>
                        <div
                            className={Wind.style({
                                backgroundPosition: "bg-center",
                                backgroundSize: "bg-cover",
                                minHeight: "min-h-52",
                                position: "relative",

                                boxShadow: "shadow-xl",
                                zIndex: "z-10",
                                display: "flex",
                                alignItems: "items-center",
                                justifyContent: "justify-center",
                            }).class}
                            style={{
                                backgroundImage: `url(${centerBackground})`
                            }}
                        >
                            <div
                                className={Wind.style({
                                    position: "absolute",
                                    left: "left-1/2",
                                    top: "top-0",
                                    borderRadius: "rounded-sm",
                                    backgroundColor: "bg-sharp-red",
                                    paddingX: "px-3",
                                    paddingY: "py-1",
                                    transformTranslateX: "-translate-x-1/2",
                                    transformTranslateY: "-translate-y-1/2",

                                    fontFamily: "font-poppins",
                                    fontWeight: "font-normal",
                                    fontSize: "text-xl",
                                    lineHeight: "leading-8",

                                    whitespace: "whitespace-nowrap",
                                }).class}
                            >
                                Create a new Headline!
                            </div>
                            <button
                                onClick={() =>
                                {
                                    previousHeadline();
                                }}
                            >
                                <PrevHeadline />
                            </button>
                            <div>
                                {history[displayed]}
                            </div>
                            <button
                                onClick={() =>
                                {
                                    nextHeadline();
                                }}
                            >
                                <NextHeadline />
                            </button>
                            {/* <HeadlineTextInputContainer
                            visible={!showHeadlineHelpers}
                            hide={() =>
                            {
                                setShowHeadlineHelpers(true);
                                document.body.classList.remove("pop-over");
                            }}
                            headline={headline}
                            submit={submitHeadline}
                        /> */}
                        </div>
                        <div
                            className={Wind.style({
                                backgroundSize: "bg-cover",
                                backgroundPosition: "bg-bottom",
                                aspectRatio: "aspect-[366_/_92]",
                                maxWidth: "max-w-[22.875rem]",
                                marginX: "mx-auto",
                                paddingX: "px-4",
                                paddingBottom: "pb-4",
                                color: "text-dark-blue",
                                position: "relative",
                            }).class}
                        >
                            <OptionsBackground
                                className={Wind.style({
                                    position: "absolute",
                                    top: "-top-[0.75rem]",
                                    left: "-left-8",
                                    zIndex: "z-0",
                                    aspectRatio: "aspect-[382_/_108]",
                                }).class}
                                style={{
                                    width: "calc(100% + 4rem)",
                                    height: "auto",
                                }}
                            />
                            <div
                                className={Wind.style({
                                    display: "grid",
                                    gridTemplateColumns: "grid-cols-3",
                                    height: "h-full",
                                    position: "relative"
                                }).class}
                            >
                                <HeadlineCreatorButton
                                    text="Create your own Headline"
                                    icon={<IconPencil className="w-6 h-6" />}
                                    onClick={() =>
                                    {
                                        setShowHeadlineHelpers(true);
                                    }}
                                />
                                <HeadlineCreatorButton
                                    text="Randomize Words"
                                    icon={<IconCharacters className="w-6 h-6" />}
                                    onClick={() =>
                                    {
                                        mixWords();
                                    }}
                                />
                                <HeadlineCreatorButton
                                    text="Randomize All"
                                    icon={<IconDice className="w-6 h-6" />}
                                    onClick={() =>
                                    {
                                        pushHeadline();
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div
                        className={Wind.style({
                            textAlign: "text-right",
                        }).class}
                    >
                        <JaggedButton
                            color="text-sharp-red"
                            className={Wind.style({
                                width: "w-1/2",
                                lineHeight: "leading-[130%]",
                                fontSize: "text-xl",
                                fontWeight: "font-bold",
                            }).class}
                            disabled={isSubmitting}
                            onClick={() =>
                            {
                                handleSubmission(headline);
                            }}
                        >
                            Create Headline
                        </JaggedButton>
                    </div>
                </Padding>
            </div>
            <HeadlineTextInputContainer
                headline={headline}
                hide={() =>
                {
                    setShowHeadlineHelpers(false);
                }}
                submit={(answer) =>
                {
                    handleSubmission(answer);
                }}
                visible={showHeadlineHelpers}
            />
        </>
    );
}
